/**
 * Utilitários compartilhados do Centro de Notificações.
 * Ícones, paleta por módulo (Linear/Attio-like), formatação de datas e buckets.
 *
 * @version 3.0.0 - 2026-04-20 — refinamento "Enterprise Premium"
 */
import {
  Inbox, RefreshCw, Users, Zap, AtSign, CalendarClock,
  FileSignature, DollarSign, ClipboardList, MessageSquare, Briefcase,
} from 'lucide-react';
import autentiqueLogo from '@/assets/autentique-logo.png';
import type { AgentNotification } from '@/hooks/useAgentNotifications';

/** Logos por slug do provider de assinatura digital. */
export const PROVIDER_LOGOS: Record<string, string> = {
  autentique: autentiqueLogo,
};

/** strokeWidth padronizado — peso de produto enterprise (não frágil). */
const STROKE = 1.75;

/** Ícone monocromático 14px por tipo de notificação. */
export function getNotificationIcon(type: AgentNotification['type'], size: 'sm' | 'md' = 'sm') {
  const cls = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';
  switch (type) {
    case 'transfer_received':   return <RefreshCw      className={cls} strokeWidth={STROKE} />;
    case 'department_transfer': return <Users          className={cls} strokeWidth={STROKE} />;
    case 'auto_assigned':       return <Zap            className={cls} strokeWidth={STROKE} />;
    case 'activity_mention':    return <AtSign         className={cls} strokeWidth={STROKE} />;
    // v1.2.0: menção em mensagem privada (nota interna) do chat
    case 'chat_note_mention':   return <AtSign         className={cls} strokeWidth={STROKE} />;
    case 'scheduling_booking':  return <CalendarClock  className={cls} strokeWidth={STROKE} />;
    case 'contract_signed':     return <FileSignature  className={cls} strokeWidth={STROKE} />;
    case 'payment_received':    return <DollarSign     className={cls} strokeWidth={STROKE} />;
    case 'task_assigned':       return <ClipboardList  className={cls} strokeWidth={STROKE} />;
    case 'task_comment_added':  return <MessageSquare  className={cls} strokeWidth={STROKE} />;
    case 'task_comment_mention':return <AtSign         className={cls} strokeWidth={STROKE} />;
    case 'deal_seller_assigned':return <Briefcase      className={cls} strokeWidth={STROKE} />;
    default:                    return <Inbox          className={cls} strokeWidth={STROKE} />;
  }
}

/**
 * Paleta tonal por módulo no estilo Linear/Attio.
 * Retorna conjunto de classes para tile/chip/dot/rail.
 *
 * Importante: usamos cores Tailwind diretas (sky/emerald/etc) com opacidade,
 * pois são tons de "categoria" — não fazem parte do tema da marca.
 * Tailwind detecta-as via JIT pois aparecem como literais no código abaixo.
 */
export interface ModulePalette {
  /** Tile colorido (32px) atrás do ícone na lista. */
  tile: string;
  /** Cor do ícone dentro do tile. */
  icon: string;
  /** Chip de kicker no detail pane. */
  chip: string;
  /** Ponto 6px (indicador de não-lida). */
  dot: string;
  /** Rail vertical 2px (item ativo). */
  rail: string;
  /** Border-left para blockquote da mensagem. */
  border: string;
}

const PALETTES: Record<string, ModulePalette> = {
  sky: {
    tile:   'bg-sky-500/10 ring-1 ring-inset ring-sky-500/20',
    icon:   'text-sky-600 dark:text-sky-400',
    chip:   'bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-1 ring-inset ring-sky-500/20',
    dot:    'bg-sky-500',
    rail:   'bg-sky-500',
    border: 'border-sky-500/60',
  },
  violet: {
    tile:   'bg-violet-500/10 ring-1 ring-inset ring-violet-500/20',
    icon:   'text-violet-600 dark:text-violet-400',
    chip:   'bg-violet-500/10 text-violet-700 dark:text-violet-300 ring-1 ring-inset ring-violet-500/20',
    dot:    'bg-violet-500',
    rail:   'bg-violet-500',
    border: 'border-violet-500/60',
  },
  amber: {
    tile:   'bg-amber-500/10 ring-1 ring-inset ring-amber-500/20',
    icon:   'text-amber-600 dark:text-amber-400',
    chip:   'bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-500/20',
    dot:    'bg-amber-500',
    rail:   'bg-amber-500',
    border: 'border-amber-500/60',
  },
  emerald: {
    tile:   'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/20',
    icon:   'text-emerald-600 dark:text-emerald-400',
    chip:   'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-inset ring-emerald-500/20',
    dot:    'bg-emerald-500',
    rail:   'bg-emerald-500',
    border: 'border-emerald-500/60',
  },
  teal: {
    tile:   'bg-teal-500/10 ring-1 ring-inset ring-teal-500/20',
    icon:   'text-teal-600 dark:text-teal-400',
    chip:   'bg-teal-500/10 text-teal-700 dark:text-teal-300 ring-1 ring-inset ring-teal-500/20',
    dot:    'bg-teal-500',
    rail:   'bg-teal-500',
    border: 'border-teal-500/60',
  },
  rose: {
    tile:   'bg-rose-500/10 ring-1 ring-inset ring-rose-500/20',
    icon:   'text-rose-600 dark:text-rose-400',
    chip:   'bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-1 ring-inset ring-rose-500/20',
    dot:    'bg-rose-500',
    rail:   'bg-rose-500',
    border: 'border-rose-500/60',
  },
  slate: {
    tile:   'bg-muted ring-1 ring-inset ring-border',
    icon:   'text-muted-foreground',
    chip:   'bg-muted text-muted-foreground ring-1 ring-inset ring-border',
    dot:    'bg-muted-foreground',
    rail:   'bg-muted-foreground',
    border: 'border-border',
  },
};

/** Mapeia tipo → chave de paleta. */
function getPaletteKey(type: AgentNotification['type']): keyof typeof PALETTES {
  switch (type) {
    case 'transfer_received':
    case 'department_transfer':
    case 'auto_assigned':       return 'sky';      // Conversas
    case 'activity_mention':
    case 'chat_note_mention':   return 'violet';   // Menções
    case 'scheduling_booking':  return 'amber';    // Agendamentos
    case 'contract_signed':     return 'emerald';  // Contratos
    case 'payment_received':    return 'teal';     // Financeiro
    case 'task_assigned':
    case 'task_comment_added':
    case 'task_comment_mention':return 'rose';     // Tarefas
    case 'deal_seller_assigned':return 'sky';      // CRM (negócios)
    default:                    return 'slate';
  }
}

/** Retorna a paleta tonal completa para um tipo. */
export function getModulePalette(type: AgentNotification['type']): ModulePalette {
  return PALETTES[getPaletteKey(type)];
}

/** @deprecated mantido por compat — use getModulePalette(t).rail */
export function getRailColor(type: AgentNotification['type']): string {
  return getModulePalette(type).rail;
}

/** Kicker uppercase do módulo (usado no detail pane). */
export function getModuleKicker(type: AgentNotification['type']): string {
  switch (type) {
    case 'transfer_received':
    case 'department_transfer':
    case 'auto_assigned':       return 'Conversa';
    case 'activity_mention':    return 'Menção';
    case 'chat_note_mention':   return 'Menção';
    case 'scheduling_booking':  return 'Agendamento';
    case 'contract_signed':     return 'Contrato';
    case 'payment_received':    return 'Financeiro';
    case 'task_assigned':
    case 'task_comment_added':
    case 'task_comment_mention':return 'Tarefa';
    case 'deal_seller_assigned':return 'Negócio';
    default:                    return 'Notificação';
  }
}

/** Formato curto para a lista. */
export function formatEditorialDate(dateString: string): string {
  const d = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.floor((startOf(now) - startOf(d)) / 86400000);

  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffDays === 0) return `${diffH}h`;
  if (diffDays === 1) return 'ontem';
  if (diffDays < 7) return `${diffDays}d`;
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' })
    .format(d)
    .replace('.', '')
    .toLowerCase();
}

/** Formato longo para o detail pane. */
export function formatEditorialDateLong(dateString: string): string {
  const d = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d).toLowerCase();
}

/** Bucket de agrupamento por data. */
export type DateBucket = 'today' | 'yesterday' | 'this_week' | 'older';

const DATE_BUCKET_LABELS: Record<DateBucket, string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  this_week: 'Esta semana',
  older: 'Mais antigas',
};

export function getDateBucket(dateString: string): DateBucket {
  const d = new Date(dateString);
  const now = new Date();
  const start = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.floor((start(now) - start(d)) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays <= 7) return 'this_week';
  return 'older';
}

export function getDateBucketLabel(bucket: DateBucket): string {
  return DATE_BUCKET_LABELS[bucket];
}

export const DATE_BUCKET_ORDER: DateBucket[] = ['today', 'yesterday', 'this_week', 'older'];
